import React, { useCallback, useEffect, useState } from 'react';
import idColumn from './ui/idColumn';
import { useNavigate } from 'react-router-dom';
import '../styles/components/ContributorDashboard.css';
import { MetricCard } from './MetricCards';
import Icon from './Icon';
import DataTable, { Column } from './DataTable';
import StatusBadge, { StatusBadgeVariant } from './ui/StatusBadge';
import { useSettings } from '../contexts/SettingsContext';
import { loadContributorsStrict, summariseContributors } from '../services/ContributorService';
import { withTimeout, LOAD_TIMEOUT_MS } from '../utils/withTimeout';
import LoadErrorBox from './ui/LoadErrorBox';
import { useExport } from './header/exporters';
import { Contributor } from '../types/ui/Settlement';
import { fmtId } from '../utils/jiraId';
import EmptyState from './EmptyState';

const ContributorDashboard: React.FC = () => {
  const { formatCurrency } = useSettings();
  const navigate = useNavigate();
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [stats, setStats] = useState({
    total_contributors: 0,
    total_contributed: 0,
    settled_count: 0,
    pending_reimbursements: 0,
    overdue_count: 0,
    central_pot: 0,
  });
  const [loading, setLoading] = useState(true);
  // 3-state (active/archived/all), matching Venues — lives in Header's
  // breadcrumb instead of a button floating in the page body below it.
  const [archiveFilter, setArchiveFilter] = useState<'active' | 'archived' | 'all'>('active');

  const [loadError, setLoadError] = useState<unknown | null>(null);

  // The request always ends: it answers, fails, or is given up on after 15 s.
  // A failure shows the error box with Retry; it never leaves "Loading…" up.
  const reload = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    withTimeout(loadContributorsStrict(), LOAD_TIMEOUT_MS, 'The contributors request')
      .then(data => {
        setContributors(data);
        setStats(summariseContributors(data));
      })
      .catch(error => {
        console.warn('Could not load contributors:', error);
        setLoadError(error ?? new Error('failed'));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('contributor-archived-sync', { detail: archiveFilter }));
  }, [archiveFilter]);

  useEffect(() => {
    const onSet = (e: Event) => setArchiveFilter((e as CustomEvent).detail);
    window.addEventListener('contributor-archived-set', onSet);
    return () => window.removeEventListener('contributor-archived-set', onSet);
  }, []);

  const statusVariant = (s: string): StatusBadgeVariant =>
    s === 'settled' ? 'success' :
    s === 'overdue'  ? 'danger'  :
    'warning';

  const goToContributor = (c: Contributor) =>
    navigate(`/payments/contributor/${fmtId('CN', c.id)}`, { state: { contributor: c } });

  const visibleContributors = contributors.filter(c => {
    if (archiveFilter === 'active') return !c.archived;
    if (archiveFilter === 'archived') return !!c.archived;
    return true;
  });

  // The Export button in the bar writes what is listed now (archived switch included); money uses the project currency.
  useExport('contributors', { rows: visibleContributors, ctx: { money: amount => formatCurrency(amount) } });

  if (loading && contributors.length === 0 && !loadError) return <div className="wt-loading">Loading contributors…</div>;
  if (loadError && contributors.length === 0) {
    return <LoadErrorBox what="contributors" error={loadError} onRetry={reload} retrying={loading} />;
  }

  const columns: Column<Contributor>[] = [
    idColumn<Contributor>('contributor'),
    {
      key: 'name',
      header: 'Name',
      width: '230px',
      render: (_v, c) => (
        <div className="cd-name-cell">
          <div className="cd-avatar">{c.name.charAt(0).toUpperCase()}</div>
          <span className="cd-name">{c.name}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Relationship',
      width: '110px',
      render: (_v, c) => c.role ? <span className="cd-role">{c.role}</span> : '—',
    },
    {
      key: 'items_paid_for',
      header: 'Note / linked item',
      width: '340px',
      render: (_v, c) => {
        if (c.team_member_name) {
          return <span className="cd-team-link" title="Linked team member">⟷ {c.team_member_name}</span>;
        }
        if (c.items_paid_for.length === 0) return <span className="cd-no-items">No items recorded</span>;
        const first = c.items_paid_for[0].description;
        const extra = c.items_paid_for.length - 1;
        return (
          <span className="cd-note-clamp" title={first}>
            {first}{extra > 0 ? ` +${extra} more` : ''}
          </span>
        );
      },
    },
    {
      key: 'total_contributed',
      header: 'Amount',
      align: 'right',
      render: (_v, c) => formatCurrency(Number(c.total_contributed)),
    },
    {
      key: 'reimbursement_status',
      header: 'Status',
      render: (_v, c) => Number(c.total_contributed) > 0
        ? <StatusBadge variant={statusVariant(c.reimbursement_status)}>{c.reimbursement_status}</StatusBadge>
        : '—',
    },
  ];

  return (
    <>
      {loadError && <LoadErrorBox what="contributors" error={loadError} onRetry={reload} retrying={loading} />}
      <section className="stats-grid">
        <MetricCard label="Total Contributors"  value={stats.total_contributors}               tone="pink"  icon="users"  />
        <MetricCard label="Total Contributed"   value={formatCurrency(stats.total_contributed)} tone="green" icon="chart"  />
        <MetricCard label="Settled"             value={stats.settled_count}                    tone="teal"  icon="check"  />
        <MetricCard label="Pending Settlement"  value={stats.pending_reimbursements}            tone="blue"  icon="alert"  />
        {/* Money contributed but not yet spent against any vendor — summed
         *  server-side from each contribution item's own `remaining`
         *  (amount minus completed payments linked to it). */}
        <MetricCard label="Central Pot"         value={formatCurrency(stats.central_pot)}       tone="purple" icon="wallet" />
      </section>

      {visibleContributors.length === 0 ? (
        <EmptyState
          icon="users"
          title={(archiveFilter === 'archived') ? 'No archived contributors' : 'No contributors yet'}
          description={(archiveFilter === 'archived')
            ? 'Contributors you archive will show up here.'
            : 'Add the people who have given money toward the project so you can track what was paid and who still needs to be reimbursed.'}
          action={!(archiveFilter === 'archived') ? (
            <button
              className="header-action-btn primary cd-empty-state-add-btn"
              onClick={() => navigate('/payments/contributor/new')}
            >
              <Icon name="plus" size={14} /> Add first contributor
            </button>
          ) : undefined}
        />
      ) : (
        <DataTable<Contributor>
          recordList={{ kind: 'contributor' }}
          data={visibleContributors}
          columns={columns}
          hoverable
          onRowClick={goToContributor}
        />
      )}
    </>
  );
};

export default ContributorDashboard;
